import { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import {
  getTimeSeries, getGenerationStats, getRevenueStats, getRewardStats, getEngagementStats, getPlatformStats, getTelemetryInsights,
  type TimeSeriesPoint, type GenerationStats, type RevenueStats, type RewardStatsData, type EngagementStats, type PlatformStats, type TelemetryInsights,
} from "@/api/adminApi";
import { StatCard } from "@/components/StatCard";
import { RangeSelector } from "@/components/RangeSelector";
import { useRange } from "@/hooks/useRange";

const GOLD = "#D4AF37";
const GOLD_MUTED = "#A68B2A";
const AMBER = "#C49A2A";

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatCurrency(cents: number): string {
  return "$" + (cents / 100).toFixed(2);
}

const tooltipStyle = {
  contentStyle: { background: "#161616", border: "1px solid #6B5F44", borderRadius: 4 },
  labelStyle: { color: "#9A8E7A", fontSize: 11 },
  itemStyle: { color: "#F0E8D8" },
};

export function AnalyticsPage() {
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useRange(30);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [userSeries, setUserSeries] = useState<TimeSeriesPoint[]>([]);
  const [creditsPurchased, setCreditsPurchased] = useState<TimeSeriesPoint[]>([]);
  const [creditsUsed, setCreditsUsed] = useState<TimeSeriesPoint[]>([]);
  const [revenueSeries, setRevenueSeries] = useState<TimeSeriesPoint[]>([]);
  const [genStats, setGenStats] = useState<GenerationStats | null>(null);
  const [revStats, setRevStats] = useState<RevenueStats | null>(null);
  const [rewardStats, setRewardStats] = useState<RewardStatsData | null>(null);
  const [engagement, setEngagement] = useState<EngagementStats | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [telemetryInsights, setTelemetryInsights] = useState<TelemetryInsights | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (days: number) => {
    setLoading(true);
    try {
      const [users, purchased, used, revenue, gens, revs, rwds, eng, plat, telem] = await Promise.all([
        getTimeSeries("new_users", days),
        getTimeSeries("credits_purchased", days),
        getTimeSeries("credits_used", days),
        getTimeSeries("revenue", days),
        getGenerationStats(days),
        getRevenueStats(days),
        getRewardStats(days),
        getEngagementStats(days),
        getPlatformStats(days),
        getTelemetryInsights(days),
      ]);
      setUserSeries(users.series);
      setCreditsPurchased(purchased.series);
      setCreditsUsed(used.series);
      setRevenueSeries(revenue.series);
      setGenStats(gens);
      setRevStats(revs);
      setRewardStats(rwds);
      setEngagement(eng);
      setPlatformStats(plat);
      setTelemetryInsights(telem);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(rangeDays); }, [rangeDays, load]);

  // Merge credits purchased & used into one series
  const creditsMerged = creditsPurchased.map((cp, i) => ({
    date: cp.date,
    purchased: cp.value,
    used: creditsUsed[i]?.value ?? 0,
  }));

  // Revenue series formatted
  const revenueFormatted = revenueSeries.map((r) => ({
    date: r.date,
    value: r.value / 100,
  }));

  // Generations by type — build stacked bar data from genStats
  const genByTypeData: { name: string; count: number }[] = [];
  if (genStats) {
    const types = Object.keys(genStats.by_type);
    // Count totals per type (ignore status breakdown for stacked bar)
    for (const t of types) {
      const statusMap = genStats.by_type[t]!;
      const total = Object.values(statusMap).reduce((a, b) => a + b, 0);
      genByTypeData.push({ name: t, count: total });
    }
  }

  // Revenue by pack
  const revByPackData = revStats
    ? Object.entries(revStats.by_pack).map(([pack, d]) => ({
        name: pack === "starter" ? "Starter" : pack === "standard" ? "Standard" : pack === "pro" ? "Pro" : pack,
        revenue: d.revenue_cents / 100,
        count: d.count,
      }))
    : [];

  // Generations by provider (horizontal bar)
  const genByProviderData = genStats
    ? Object.entries(genStats.by_provider)
        .sort(([, a], [, b]) => b - a)
        .map(([provider, count]) => ({ name: provider, count }))
    : [];

  return (
    <div>
      {/* Range selector + refresh */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <RangeSelector value={rangeDays} onChange={setRangeDays} />
        <button className="btn-outline" onClick={() => load(rangeDays)} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
        {lastUpdated && (
          <span style={{ fontSize: 11, color: "#9A8E7A" }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {loading && <div className="loading">Rolling film...</div>}

      {!loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Row 1: New Users + Credits */}
          <div className="grid-2">
            <div className="card chart-card">
              <h3>New Users</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={userSeries}>
                  <defs>
                    <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(d: string) => new Date(d).toLocaleDateString()} />
                  <Area type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2} fill="url(#gGold)" name="New Users" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="card chart-card">
              <h3>Credits: Purchased vs Used</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={creditsMerged}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(d: string) => new Date(d).toLocaleDateString()} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#9A8E7A" }} />
                  <Line type="monotone" dataKey="purchased" stroke={GOLD} strokeWidth={2} dot={false} name="Purchased" />
                  <Line type="monotone" dataKey="used" stroke={GOLD_MUTED} strokeWidth={2} dot={false} name="Used" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 2: Revenue + Generations by Type */}
          <div className="grid-2">
            <div className="card chart-card">
              <h3>Credit Revenue (packs only)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenueFormatted}>
                  <defs>
                    <linearGradient id="gAmber" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={AMBER} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={AMBER} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v: number) => `$${v}`} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(d: string) => new Date(d).toLocaleDateString()} formatter={(v: number) => [formatCurrency(v * 100), "Revenue"]} />
                  <Area type="monotone" dataKey="value" stroke={AMBER} strokeWidth={2} fill="url(#gAmber)" name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="card chart-card">
              <h3>AI Jobs by Type{genStats?.total_jobs != null ? ` — ${genStats.total_jobs.toLocaleString()} in range` : ""}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={genByTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} name="Generations" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 3: Revenue by Pack + Generations by Model */}
          <div className="grid-2">
            <div className="card chart-card">
              <h3>Revenue by Pack</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revByPackData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v: number) => `$${v}`} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} formatter={(v: number) => [`$${v.toFixed(2)}`, "Revenue"]} />
                  <Bar dataKey="revenue" fill={GOLD_MUTED} radius={[4, 4, 0, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card chart-card">
              <h3>AI Jobs by Model</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={genByProviderData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} width={120} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill={GOLD} radius={[0, 4, 4, 0]} name="Generations" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 3b: AI Jobs by Environment */}
          {genStats && (() => {
            const envEntries = Object.entries(genStats.by_source_env ?? {});
            const total = envEntries.reduce((s, [, n]) => s + n, 0);
            if (total === 0) return null;
            return (
              <div className="card chart-card" style={{ maxWidth: 420 }}>
                <h3>AI Jobs by Environment</h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#9A8E7A", fontSize: 10, letterSpacing: "0.05em" }}>
                      <th style={{ padding: "8px 4px", fontWeight: 600 }}>ENV</th>
                      <th style={{ padding: "8px 4px", fontWeight: 600, textAlign: "right" }}>JOBS</th>
                      <th style={{ padding: "8px 4px", fontWeight: 600, textAlign: "right" }}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {envEntries.sort(([a], [b]) => a.localeCompare(b)).map(([env, count]) => (
                      <tr key={env} style={{ borderTop: "1px solid #3A3428" }}>
                        <td style={{ padding: "10px 4px", color: "#C49A2A", fontWeight: 600 }}>{env}</td>
                        <td style={{ padding: "10px 4px", textAlign: "right", color: "#F0E8D8", fontVariantNumeric: "tabular-nums" }}>
                          {count.toLocaleString()}
                        </td>
                        <td style={{ padding: "10px 4px", textAlign: "right", color: "#9A8E7A", fontVariantNumeric: "tabular-nums" }}>
                          {((count / total) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Row 4: Weekly Active Users */}
          {engagement && engagement.wau_series.length > 0 && (
            <div className="card chart-card">
              <h3>Weekly Generating Accounts (trailing 7d)</h3>
              <p style={{ fontSize: 11, color: "#9A8E7A", margin: "0 0 8px" }}>
                Distinct accounts with at least one AI job in the prior 7 days. Not &ldquo;opened the app&rdquo;: lapsed free-mode users cannot generate and never appear here.
              </p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={engagement.wau_series}>
                  <defs>
                    <linearGradient id="gGoldWau" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(d: string) => new Date(d).toLocaleDateString()} />
                  <Area type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2} fill="url(#gGoldWau)" name="WAU" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Row 5: Generation Quality — Failure Rate by Type */}
          {genStats && (() => {
            const failureData = Object.entries(genStats.by_type).map(([type, statusMap]) => {
              const total = Object.values(statusMap).reduce((a, b) => a + b, 0);
              const failed = statusMap["failed"] ?? 0;
              return { name: type, rate: total > 0 ? Math.round((failed / total) * 100) : 0, failed, total };
            }).filter((d) => d.total > 0);

            return failureData.length > 0 ? (
              <div className="card chart-card">
                <h3>Failure Rate by Type (%)</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={failureData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} width={100} />
                    <Tooltip {...tooltipStyle} formatter={(v: number) => [`${v}%`, "Failure Rate"]} />
                    <Bar dataKey="rate" fill="#C44040" radius={[0, 4, 4, 0]} name="Failure Rate" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : null;
          })()}

          {/* Row 5: Reward Program */}
          {rewardStats && (
            <>
              <h3 className="section-title" style={{ marginTop: 12 }}>Reward Program</h3>
              <div className="grid-3">
                <StatCard
                  label="Total Reward Events"
                  value={rewardStats.total_reward_events.toString()}
                />
                <StatCard
                  label="Credits from Rewards"
                  value={rewardStats.credits_rewards_vs_purchased.rewards.toLocaleString()}
                  sub="vs purchased"
                />
                <StatCard
                  label="Credits Purchased"
                  value={rewardStats.credits_rewards_vs_purchased.purchased.toLocaleString()}
                  sub="in period"
                />
              </div>
              {rewardStats.top_referrers.length > 0 && (
                <div className="card" style={{ marginTop: 16 }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Top Referrers</th><th>Referrals</th></tr>
                    </thead>
                    <tbody>
                      {rewardStats.top_referrers.slice(0, 5).map((r) => (
                        <tr key={r.user_id} className="clickable" onClick={() => navigate(`/users/${r.user_id}`)}>
                          <td style={{ color: "var(--gold)" }}>{r.email}</td>
                          <td>{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {/* Row 7: Feature Adoption */}
          {platformStats && (
            <>
              <h3 className="section-title" style={{ marginTop: 12 }}>Feature Adoption</h3>
              <p style={{ fontSize: 11, color: "#9A8E7A", margin: "0 0 8px" }}>
                Exact row counts created in the range: review sessions (share links), team projects (embedded team sharing), published exports (Publish to Web).
              </p>
              <div className="grid-3">
                <StatCard
                  label="Review Sessions"
                  value={platformStats.review_sessions.toString()}
                  sub="in period"
                />
                <StatCard
                  label="Team Projects"
                  value={platformStats.team_projects.toString()}
                  sub={`${platformStats.team_projects_ready} ready`}
                />
                <StatCard
                  label="Published Exports"
                  value={platformStats.published_exports.toString()}
                  sub="in period"
                />
              </div>
            </>
          )}

          {telemetryInsights && (
            <>
              <h3 className="section-title" style={{ marginTop: 12 }}>Telemetry Insights</h3>
              <div className="grid-2">
                <div className="card chart-card">
                  <h3>Depletion Frequency</h3>
                  {telemetryInsights.depletion_daily.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", color: "#9A8E7A", fontSize: 12 }}>
                      No depletion events in range.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={telemetryInsights.depletion_daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                        <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                        <Tooltip {...tooltipStyle} labelFormatter={(d: string) => new Date(d).toLocaleDateString()} />
                        <Legend wrapperStyle={{ fontSize: 11, color: "#9A8E7A" }} />
                        <Line type="monotone" dataKey="timeline" stroke={GOLD} strokeWidth={2} dot={false} name="Timeline" />
                        <Line type="monotone" dataKey="preview" stroke={AMBER} strokeWidth={2} dot={false} name="Preview" />
                        <Line type="monotone" dataKey="ai_panel" stroke={GOLD_MUTED} strokeWidth={2} dot={false} name="AI Panel" />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="card chart-card">
                  <h3>Project Source Mix</h3>
                  <p style={{ fontSize: 11, color: "#9A8E7A", margin: "0 0 8px" }}>
                    Distinct devices per source of the projects they opened. &ldquo;Imported&rdquo; = opened via Open With / file import.
                  </p>
                  {(() => {
                    const ps = telemetryInsights.project_source;
                    const total = ps.sample + ps.user_project + ps.imported;
                    if (total === 0) {
                      return (
                        <div style={{ padding: 24, textAlign: "center", color: "#9A8E7A", fontSize: 12 }}>
                          No project opens in range.
                        </div>
                      );
                    }
                    const rows: Array<{ label: string; count: number }> = [
                      { label: "Sample", count: ps.sample },
                      { label: "User Project", count: ps.user_project },
                      { label: "Imported", count: ps.imported },
                    ];
                    return (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr style={{ textAlign: "left", color: "#9A8E7A", fontSize: 10, letterSpacing: "0.05em" }}>
                            <th style={{ padding: "8px 4px", fontWeight: 600 }}>SOURCE</th>
                            <th style={{ padding: "8px 4px", fontWeight: 600, textAlign: "right" }}>DEVICES</th>
                            <th style={{ padding: "8px 4px", fontWeight: 600, textAlign: "right" }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.label} style={{ borderTop: "1px solid #3A3428" }}>
                              <td style={{ padding: "10px 4px", color: "#C49A2A", fontWeight: 600 }}>{r.label}</td>
                              <td style={{ padding: "10px 4px", textAlign: "right", color: "#F0E8D8", fontVariantNumeric: "tabular-nums" }}>
                                {r.count.toLocaleString()}
                              </td>
                              <td style={{ padding: "10px 4px", textAlign: "right", color: "#9A8E7A", fontVariantNumeric: "tabular-nums" }}>
                                {((r.count / total) * 100).toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
